import type { ReactNode } from 'react';
import { Icon } from './icon/index.ts';
import { Text } from './Text.tsx';
import styles from './Input.module.css';

/**
 * The label, control and message of a form field, in the arrangement § 14
 * fixes: the label is ALWAYS VISIBLE ABOVE THE FIELD — never a placeholder
 * standing in for it — and an error is always specific text anchored to the
 * field, never a colour alone.
 *
 * Field owns the wiring (label association, `aria-describedby`); the control
 * it wraps owns its own appearance.
 */

interface FieldProps {
  /** The id of the control this field labels. */
  htmlFor: string;
  label: string;
  /** Specific and textual. "Required" is not a message; "Enter a title" is. */
  error?: string;
  hint?: string;
  children: ReactNode;
  /**
   * § 16 — the administrative density.
   *
   * `default` is § 5.1's `subheading`, which is right where a form is the
   * screen: three fields, each its own decision. `compact` drops the label to
   * `body-sm` at label weight, for a form of a dozen fields inside named
   * sections — there, a 19px label is LOUDER than the section heading above
   * it, which inverts the hierarchy the sections exist to create.
   */
  size?: 'default' | 'compact';
}

export function Field({ htmlFor, label, error, hint, children, size = 'default' }: FieldProps) {
  return (
    <div className={styles.field} data-size={size}>
      <label
        htmlFor={htmlFor}
        className={size === 'compact' ? styles.compactLabel : 'type-subheading'}
      >
        {label}
      </label>
      {children}
      {hint && !error ? (
        <Text id={`${htmlFor}-hint`} variant="body-sm" tone="muted">
          {hint}
        </Text>
      ) : null}
      {error ? (
        <span className={styles.message} id={`${htmlFor}-error`} role="alert">
          <Icon name="alert-circle" size={16} />
          <Text variant="body-sm" tone="error">
            {error}
          </Text>
        </span>
      ) : null}
    </div>
  );
}
